import { useState } from 'react';
import type { LearnMode, Prefs } from '../../lib/types.js';

type Props = {
  prefs: Prefs | null;
  loading: boolean;
  error: string | null;
  setPref: (patch: Partial<Prefs>) => Promise<void>;
};

const OPTIONS: ReadonlyArray<{ value: LearnMode; label: string }> = [
  { value: 'independent', label: '独立' },
  { value: 'mixed', label: '混合' },
];

export function PrefsModeToggle({ prefs, loading, error, setPref }: Props): JSX.Element {
  const [saving, setSaving] = useState<LearnMode | null>(null);
  const current = prefs?.learn_mode ?? null;

  const handlePick = async (next: LearnMode) => {
    if (!prefs || prefs.learn_mode === next || saving) return;
    setSaving(next);
    try {
      await setPref({ learn_mode: next });
    } catch {
      // setPref already surfaces error via hook state.
    } finally {
      setSaving(null);
    }
  };

  return (
    <section aria-label="学习模式">
      <h2 className="text-secondary text-hint mb-2">学习模式</h2>
      <div
        role="radiogroup"
        aria-label="学习模式"
        className="bg-surface-2 border border-border rounded-full p-1 flex"
      >
        {OPTIONS.map((opt) => {
          const active = current === opt.value;
          const isSaving = saving === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={loading || saving !== null}
              onClick={() => void handlePick(opt.value)}
              className={[
                'flex-1 h-10 rounded-full text-sentence transition-colors',
                active
                  ? 'bg-primary text-canvas'
                  : 'text-secondary active:bg-surface',
                loading || saving !== null ? 'opacity-60' : '',
              ].join(' ')}
            >
              {isSaving ? '...' : opt.label}
            </button>
          );
        })}
      </div>
      {loading ? (
        <div className="text-muted text-hint mt-2">载入中...</div>
      ) : error ? (
        <div className="text-secondary text-hint mt-2 break-words">加载失败 · {error}</div>
      ) : null}
    </section>
  );
}
